// DESIGN SYSTEM (BLOGGER-CARD-DS 2026-08-18, spec P4). The card's message bubble, rewritten
// onto the OutCRM design system. It is card-exclusive — the only importers are the Email tab
// and the (unreachable) Instagram tab — so the whole component moves to `oc-lead-card__msg*`
// classes in components/lead-card.css and no inner element keeps a legacy class. The
// `.activity*` rule family stays in styles.css for the screens that still emit those names
// (spec S7, ledger buckets 2 and 4).
//
// Recorded consequence of the Instagram freeze (spec S5): LeadDetailsInstagramTab.jsx mounts
// this component and stays byte-identical. The tab is unreachable, so nothing renders the
// combination; the only check owed is that it remains hidden (AC-21).
//
// The migration is presentation-only (fixed point F3): every parse helper, every condition,
// the collapse threshold and every RU string are carried over unchanged. The `↩` reply marks
// and the `···` quote indicator are dingbats outside the spec S6 map and are carried verbatim,
// exactly as the P3 slice carried `↻ ↗ → …`.
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { formatDateTime } from '../utils.js';
import { parseEmailBody, parseQuotedBlock, getQuoteFirstLine, hasHtml, sanitizeHtml, stripLeadingSubject } from '../utils/emailBody';

/** Collapsible HTML body — shows max 150px with fade + "Развернуть" */
const HtmlBodyCollapsible = ({ html }) => {
    const ref = useRef(null);
    const [overflows, setOverflows] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (ref.current) {
            setOverflows(ref.current.scrollHeight > 160);
        }
    }, [html]);

    return (
        <div className={`oc-lead-card__msg-html-wrap${!expanded && overflows ? ' oc-lead-card__msg-html-wrap--collapsed' : ''}`}>
            <div
                ref={ref}
                className='oc-lead-card__msg-html'
                dangerouslySetInnerHTML={{ __html: html }}
            />
            {overflows && !expanded && (
                <button className='oc-lead-card__msg-expand' type='button' onClick={() => setExpanded(true)}>
                    Развернуть
                </button>
            )}
            {expanded && overflows && (
                <button className='oc-lead-card__msg-expand' type='button' onClick={() => setExpanded(false)}>
                    Свернуть
                </button>
            )}
        </div>
    );
};

export const ActivityItem = ({ message, onReply }) => {
    const [showQuote, setShowQuote] = useState(false);

    const outbound = message.direction === 'out';
    const payloadType = message?.payload?.type;
    const isError = message.direction === 'system' || message.status === 'error';
    const isEmail = message.channel === 'email';

    // Reply detection — inbound OR outbound manual reply.
    // legacy data fallback: historical LeadMessage rows carry payloadType='instantly_email_reply'.
    const isReply = isEmail && (
        payloadType === 'instantly_email_reply' ||
        payloadType === 'replyio_email_reply' ||
        payloadType === 'email_manual_reply' ||
        (message.payload?.subject || '').toLowerCase().startsWith('re:')
    );

    // Show reply button on inbound email messages
    const canReply = isEmail && !outbound && !isError;

    const variantClass = isError
        ? 'oc-lead-card__msg--error'
        : outbound ? 'oc-lead-card__msg--manager'
        : 'oc-lead-card__msg--client';

    const displayAuthor = (() => {
        if (isError) return 'Ошибка';
        if (outbound) return message.payload?.fromName || message.author || 'Менеджер';
        return message.author || 'Лид';
    })();

    const channelLabel = isError ? 'Ошибка' : (message.channel || '');
    const subject = isEmail ? (message.payload?.subject || null) : null;

    const rawBodyText = message.bodyText || '';

    const { main: bodyMain, quoted: bodyQuoted } = useMemo(
        () => isEmail ? parseEmailBody(rawBodyText) : { main: rawBodyText, quoted: null },
        [isEmail, rawBodyText]
    );

    // Strip subject duplicate from beginning of body
    const cleanedMain = useMemo(
        () => isEmail ? stripLeadingSubject(bodyMain, subject) : bodyMain,
        [isEmail, bodyMain, subject]
    );

    // Parse quoted block into attribution header and actual quoted content
    const { header: quotedHeader, content: quotedContent } = useMemo(
        () => parseQuotedBlock(bodyQuoted),
        [bodyQuoted]
    );

    const quoteFirstLine = useMemo(() => getQuoteFirstLine(quotedContent), [quotedContent]);

    const displayBody = cleanedMain || rawBodyText || '(пусто)';
    const htmlContent = message.bodyHtml || null;
    const bodyIsHtml = isEmail && (htmlContent || hasHtml(displayBody));

    return (
        <div className={['oc-lead-card__msg', variantClass].filter(Boolean).join(' ')}>
            <div className='oc-lead-card__msg-meta'>
                {isReply && (
                    <span className='oc-lead-card__msg-reply-mark' title='Ответ на письмо'>↩</span>
                )}
                <span className='oc-lead-card__msg-author'>{displayAuthor}</span>
                {channelLabel && <span className='oc-lead-card__msg-channel'>{channelLabel}</span>}
                <span className='oc-lead-card__msg-time'>{formatDateTime(message.createdAt)}</span>
            </div>

            {/* Subject line — amber/yellow */}
            {subject && (
                <div className='oc-lead-card__msg-subject'>{subject}</div>
            )}

            {message.payload?.sourceCampaignName && (
                <div className='oc-lead-card__msg-campaign'>
                    из кампании: {message.payload.sourceCampaignName}
                </div>
            )}

            <div className='oc-lead-card__msg-body'>
                {/* Main body */}
                {bodyIsHtml
                    ? <HtmlBodyCollapsible html={sanitizeHtml(htmlContent || displayBody)} />
                    : <span className='oc-lead-card__msg-text'>{displayBody}</span>
                }

                {/* Quote section */}
                {bodyQuoted && (
                    <div className='oc-lead-card__msg-quote-wrap'>
                        {/* Attribution header (date/sender) shown below main body */}
                        {quotedHeader && (
                            <div className='oc-lead-card__msg-quote-head'>{quotedHeader}</div>
                        )}

                        {/* Toggle button — compact "···" pill */}
                        <button
                            className='oc-lead-card__msg-quote-toggle'
                            onClick={() => setShowQuote(v => !v)}
                            title={showQuote ? 'Скрыть' : 'Показать цитируемое письмо'}
                        >
                            <span className='oc-lead-card__msg-quote-dots'>···</span>
                            {!showQuote && quoteFirstLine && (
                                <span className='oc-lead-card__msg-quote-preview'>{quoteFirstLine}</span>
                            )}
                        </button>

                        {/* Expanded quoted content — > stripped, left bar style */}
                        {showQuote && (
                            <div className='oc-lead-card__msg-quote'>
                                <div className='oc-lead-card__msg-quote-text'>
                                    {quotedContent || bodyQuoted}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {canReply && onReply && (
                <button
                    className='oc-lead-card__msg-reply-btn'
                    type='button'
                    onClick={() => onReply(message)}
                    title='Ответить на это сообщение'
                >
                    ↩ Ответить
                </button>
            )}
        </div>
    );
};
